/**
 * ProvidersPage — "Modell-Routing": pick which model handles which task.
 * Tab 1 "Global": defaults for all companies. Tab 2 "Pro Firma": per-company override.
 * Each task = one row. Save upserts into db.providers (company_id null or specific).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Save, RotateCcw, Cpu, MessageSquareText, Film, Hash,
  Image as ImageIcon, Video, Mic, Languages, Eye, Lightbulb, Building2, Plus, ShieldCheck, Zap
} from "lucide-react";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "gemini", label: "Google Gemini" },
];

const MODELS_BY_PROVIDER = {
  openai: ["gpt-5.2", "gpt-5.1", "gpt-5", "gpt-4.1", "gpt-4o", "sora-2", "sora-2-pro"],
  anthropic: ["claude-sonnet-4-5-20250929", "claude-opus-4-5-20251101", "claude-haiku-4-5-20251001"],
  gemini: ["gemini-3-flash-preview", "gemini-3.1-flash-image-preview", "gemini-2.5-pro", "gemini-2.5-flash"],
};

const TASKS = [
  { key: "caption", icon: MessageSquareText, label: "Captions / Posts", desc: "Instagram & Facebook Caption-Texte im Wizard", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "reel", icon: Film, label: "Reels-Konzepte", desc: "Hook · Szenen · Call-to-Action für Video-Reels", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "hashtag", icon: Hash, label: "Hashtag-Vorschlaege", desc: "KI-Hashtags pro Post-Caption", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "image_gen", icon: ImageIcon, label: "Bild-Generierung", desc: "Inline KI-Bilder im Media-Wizard", default: { provider: "gemini", model: "gemini-3.1-flash-image-preview" } },
  { key: "video_gen", icon: Video, label: "Video-Generierung", desc: "KI-Reel-Videos (z.B. Veo, Sora)", default: { provider: "openai", model: "sora-2" } },
  { key: "voice", icon: Mic, label: "Brand-Voice-Training", desc: "Stilanalyse aus Beispiel-Posts + Voice-Drafts", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "translate", icon: Languages, label: "Uebersetzung", desc: "Caption-Translation DE↔EN/IT/FR/ES", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "image_analysis", icon: Eye, label: "Bild-Analyse (Vision)", desc: "Auto-Tagging hochgeladener Bilder", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "company_research", icon: Lightbulb, label: "Firmen-Auto-Fill", desc: "Markenprofil aus Website + Brief", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "topics", icon: Lightbulb, label: "Themenvorschlaege", desc: "Wochen-Themen für eine Firma", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "competitor", icon: Eye, label: "Wettbewerbs-Analyse", desc: "Konkurrenz-Inhalte interpretieren", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "weekly_plan", icon: MessageSquareText, label: "Wochenplan", desc: "Kalender-Vorschläge für 7 Tage", default: { provider: "openai", model: "gpt-5.2" } },
  { key: "eval", icon: Eye, label: "RSS-Relevanz / Eval", desc: "Score Artikel vs. Marken-Brief", default: { provider: "openai", model: "gpt-5.2" } },
];

export default function ProvidersPage() {
  const [allProviders, setAllProviders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [ownKeys, setOwnKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("global");
  const [companyId, setCompanyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes, kRes] = await Promise.all([
        axios.get(`${API}/providers`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/admin/llm-keys`, { withCredentials: true }).catch(() => ({ data: { providers: [] } })),
      ]);
      setAllProviders(pRes.data || []);
      setCompanies(cRes.data || []);
      const own = new Set((kRes.data?.providers || []).filter((p) => p.configured).map((p) => p.provider));
      setOwnKeys(own);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Map: routes for given scope (companyId === null = global)
  const routesFor = (cid) => {
    const m = {};
    for (const p of allProviders) {
      const isGlobal = !p.company_id;
      if (cid === null && !isGlobal) continue;
      if (cid !== null && p.company_id !== cid) continue;
      if (TASKS.find((t) => t.key === p.task_type)) {
        m[p.task_type] = { provider: p.provider, model: p.model, id: p.id };
      }
    }
    return m;
  };

  const globalRoutes = useMemo(() => routesFor(null), [allProviders]); // eslint-disable-line
  const companyRoutes = useMemo(() => routesFor(companyId || null), [allProviders, companyId]); // eslint-disable-line
  const companyName = companies.find((c) => c.id === companyId)?.name || "";

  return (
    <div data-testid="providers-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight flex items-center gap-3">
          <Cpu className="w-8 h-8 text-[#2563EB]" /> Modell-Routing
        </h1>
        <p className="text-[var(--studio-text-muted)] mt-1">
          Wähle pro Aufgabe das beste KI-Modell. Routing nutzt automatisch
          den passenden API-Schluessel aus der <a href="/admin/llm-keys" className="text-[#2563EB] hover:underline">KI-API-Schluessel</a>-Konfiguration.
        </p>
      </div>

      <PageHelp title="Wie funktioniert das Routing? Wann brauche ich was?" storageKey="model-routing">
        <p>
          Hier legst du fest, <b>welcher Provider und welches Modell</b> fuer jede Aufgabe verwendet wird.
          Die KI-API-Schluessel selbst hinterlegst du unter <a href="/admin/llm-keys">KI-API-Schluessel</a>.
        </p>
        <h4>Zwei Ebenen</h4>
        <ul>
          <li><b>Tab „Global"</b> — Default fuer alle Firmen. Beispiel: „Bilder generieren = Gemini Nano Banana".</li>
          <li><b>Tab „Pro Firma"</b> — Override fuer einzelne Firmen. Beispiel: „BellaStrada bekommt Captions ueber Claude statt GPT".</li>
        </ul>
        <h4>Aufgaben-Liste (was wird wo gebraucht)</h4>
        <ul>
          <li><b>caption</b> — alle Post-Texte im Wizard und Auto-Fill</li>
          <li><b>hashtag</b> — Hashtag-Vorschlaege</li>
          <li><b>reel</b> — Reel-Hooks &amp; -Skripte</li>
          <li><b>image_gen</b> — „KI-Bild erzeugen"-Button</li>
          <li><b>video_gen</b> — „KI-Video erzeugen"-Button</li>
          <li><b>image_analysis</b> — Auto-Tagging + Focal-Point beim Bild-Upload</li>
          <li><b>translate</b> — Sprachwechsel (DE/AT/EN)</li>
          <li><b>weekly_plan</b> — Wochenplaner-Auto-Fill</li>
          <li><b>competitor</b> — Wettbewerber-Analyse</li>
          <li><b>company_research</b> — Firmen-Website-Scraping bei Neuanlage</li>
        </ul>
        <h4>Empfohlene Default-Einstellung</h4>
        <ol>
          <li>Texte (caption, hashtag, reel, translate, …) → <b>OpenAI / gpt-5.2</b></li>
          <li>Bilder generieren → <b>Gemini / nano-banana</b> (super guenstig, hohe Qualitaet)</li>
          <li>Bilder analysieren → <b>Gemini / gemini-2.5-flash</b> oder <b>OpenAI / gpt-5.2</b></li>
          <li>Videos → <b>OpenAI / sora-2</b></li>
        </ol>
        <p>
          Falls du etwas anderes ausprobieren willst (z. B. Flux statt Gemini fuer Bilder, oder Claude statt GPT fuer kreativere Captions),
          einfach hier umstellen → ab dem naechsten Aufruf laeuft's automatisch ueber den neuen Provider.
        </p>
      </PageHelp>

      <Tabs value={tab} onValueChange={setTab} data-testid="routing-tabs">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="global" data-testid="tab-global">
            <Cpu className="w-3.5 h-3.5 mr-1.5" /> Global
          </TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company">
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> Pro Firma
          </TabsTrigger>
        </TabsList>

        <TabsContent value="global" className="space-y-3 mt-4">
          <p className="text-xs text-[var(--studio-text-muted)]">
            Globale Routing-Regeln gelten für alle Firmen, sofern keine firmenspezifische Regel existiert.
          </p>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-[var(--studio-text-muted)]" />
          ) : (
            <RoutingList
              scope="global"
              companyId={null}
              routes={globalRoutes}
              fallbacks={null}
              ownKeys={ownKeys}
              onChanged={load}
            />
          )}
        </TabsContent>

        <TabsContent value="company" className="space-y-3 mt-4">
          <Card className="border-[var(--studio-border)] shadow-none">
            <CardContent className="p-3 flex items-center gap-3">
              <Building2 className="w-4 h-4 text-[var(--studio-text-muted)]" />
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="max-w-sm h-9 text-sm" data-testid="company-picker">
                  <SelectValue placeholder="Firma waehlen..." />
                </SelectTrigger>
                <SelectContent>
                  {companies.length === 0 && <SelectItem value="__empty__" disabled>Keine Firmen vorhanden</SelectItem>}
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {companyId && (
                <Badge className="bg-[#2563EB]/10 text-[#2563EB] border-0 text-[10px]">
                  {Object.keys(companyRoutes).length} Override(s)
                </Badge>
              )}
            </CardContent>
          </Card>
          {!companyId ? (
            <Card className="border-dashed border-[var(--studio-border)] shadow-none">
              <CardContent className="py-12 text-center text-[var(--studio-text-muted)]">
                <Building2 className="w-8 h-8 mx-auto opacity-40 mb-2" />
                <p className="text-sm">Wähle eine Firma um deren Routing-Overrides zu sehen.</p>
                <p className="text-xs mt-1">Aufgaben ohne Override nutzen das globale Routing.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <p className="text-xs text-[var(--studio-text-muted)]">
                Overrides für <b>{companyName}</b>. Aufgaben ohne Custom-Modell nutzen das globale Routing als Fallback.
              </p>
              <RoutingList
                scope="company"
                companyId={companyId}
                routes={companyRoutes}
                fallbacks={globalRoutes}
                ownKeys={ownKeys}
                onChanged={load}
              />
            </>
          )}
        </TabsContent>
      </Tabs>

      <Card className="border-[var(--studio-border)] shadow-none bg-[var(--studio-bg)]/40">
        <CardHeader>
          <CardTitle className="font-heading text-sm">Routing-Hierarchie</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-[var(--studio-text-muted)] space-y-1">
          <p>1. <b>Pro-Firma-Override</b> (höchste Priorität, in "Pro Firma"-Tab)</p>
          <p>2. <b>Globale Routing-Regel</b> (im "Global"-Tab definiert)</p>
          <p>3. <b>Hardcoded Default</b> (siehe Beschreibung neben jeder Aufgabe)</p>
        </CardContent>
      </Card>
    </div>
  );
}

function RoutingList({ scope, companyId, routes, fallbacks, ownKeys, onChanged }) {
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  // Reset edits when switching scope/company
  useEffect(() => { setEdits({}); }, [scope, companyId]);

  const effective = (key) => {
    const t = TASKS.find((x) => x.key === key);
    return edits[key] || routes[key] || (fallbacks ? fallbacks[key] : null) || t.default;
  };
  const status = (key) => {
    if (edits[key]) return "dirty";
    if (routes[key]) return "custom";
    if (fallbacks && fallbacks[key]) return "global";
    return "default";
  };

  const setEdit = (key, patch) => {
    setEdits((e) => {
      const cur = e[key] || routes[key] || (fallbacks ? fallbacks[key] : null) || TASKS.find((x) => x.key === key).default;
      return { ...e, [key]: { ...cur, ...patch } };
    });
  };

  const saveOne = async (key) => {
    const cfg = edits[key];
    if (!cfg) return;
    setSavingKey(key);
    try {
      const existing = routes[key];
      const t = TASKS.find((x) => x.key === key);
      const payload = {
        name: scope === "company" ? `Routing (Firma): ${t.label}` : `Routing: ${t.label}`,
        provider: cfg.provider,
        model: cfg.model,
        task_type: key,
        company_id: companyId || null,
        is_default: false,
      };
      if (existing?.id) {
        await axios.put(`${API}/providers/${existing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/providers`, payload, { withCredentials: true });
      }
      toast.success(`${t.label} → ${cfg.provider}/${cfg.model}`);
      setEdits((e) => { const n = { ...e }; delete n[key]; return n; });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSavingKey(null);
  };

  const resetOne = async (key) => {
    if (edits[key] && !routes[key]) {
      setEdits((e) => { const n = { ...e }; delete n[key]; return n; });
      return;
    }
    const existing = routes[key];
    if (!existing?.id) return;
    const msg = scope === "company"
      ? "Override entfernen? Aufgabe nutzt dann wieder das globale Routing."
      : "Globales Routing entfernen? Aufgabe nutzt dann den Hardcoded-Default.";
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/providers/${existing.id}`, { withCredentials: true });
      toast.success("Zurueckgesetzt");
      setEdits((e) => { const n = { ...e }; delete n[key]; return n; });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zuruecksetzen fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-2" data-testid="task-routing-list">
      {TASKS.map(({ key, icon: Icon, label, desc }) => {
        const cur = effective(key);
        const st = status(key);
        return (
          <Card
            key={key}
            className={`border-[var(--studio-border)] shadow-none ${st === "dirty" ? "ring-1 ring-[#2563EB]/30" : ""}`}
            data-testid={`task-row-${key}`}
          >
            <CardContent className="p-3 flex items-center gap-3 flex-wrap">
              <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-[#2563EB]" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium">{label}</p>
                  {st === "custom" && (
                    <Badge className="bg-[#2563EB]/10 text-[#2563EB] border-0 text-[9px]">
                      {scope === "company" ? "Firma-Override" : "Custom"}
                    </Badge>
                  )}
                  {st === "global" && (
                    <Badge variant="outline" className="text-[9px] text-[var(--studio-text-muted)]">Global</Badge>
                  )}
                  {st === "default" && (
                    <Badge variant="outline" className="text-[9px] text-[var(--studio-text-muted)]">Standard</Badge>
                  )}
                  {st === "dirty" && (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-[9px]">Geaendert</Badge>
                  )}
                  {ownKeys?.has(cur.provider) ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[9px]" title="Eigener API-Schluessel aktiv" data-testid={`key-own-${scope}-${key}`}>
                      <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> Eigener Key
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-0 text-[9px]" title="Emergent-Universal-Key (Fallback)" data-testid={`key-emergent-${scope}-${key}`}>
                      <Zap className="w-2.5 h-2.5 mr-0.5" /> Emergent
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-[var(--studio-text-muted)]">{desc}</p>
              </div>

              <Select
                value={cur.provider}
                onValueChange={(v) => setEdit(key, { provider: v, model: MODELS_BY_PROVIDER[v]?.[0] || "" })}
              >
                <SelectTrigger className="w-32 h-8 text-xs" data-testid={`provider-${scope}-${key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select
                value={cur.model}
                onValueChange={(v) => setEdit(key, { model: v })}
              >
                <SelectTrigger className="w-52 h-8 text-xs font-mono" data-testid={`model-${scope}-${key}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(MODELS_BY_PROVIDER[cur.provider] || []).map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="button"
                size="sm"
                onClick={() => saveOne(key)}
                disabled={!edits[key] || savingKey === key}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-8 text-xs"
                data-testid={`save-${scope}-${key}-btn`}
              >
                {savingKey === key ? <Loader2 className="w-3 h-3 animate-spin" /> :
                  (st === "global" || st === "default") ? <Plus className="w-3 h-3" /> : <Save className="w-3 h-3" />
                }
              </Button>
              {(st === "custom" || st === "dirty") && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => resetOne(key)}
                  className="h-8 text-xs"
                  title={scope === "company" ? "Override entfernen (faellt zurueck auf Global)" : "Zurueck auf Hardcoded-Default"}
                  data-testid={`reset-${scope}-${key}-btn`}
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
